/**
 * The pending-deletion gate (118-07): the decision, kept pure so every branch
 * is testable without a router.
 *
 * While an account has an open deletion request, signing in works but the only
 * places it may go are the pending screen (Cancel deletion / Sign out), the
 * steps that lead there, and read-only legal pages. The gate sits on the ROOT
 * navigator, so a deep link or a web refresh to any app route lands on the
 * pending screen too.
 *
 * LOADING IS NOT "NO REQUEST" (same principle as the P0-4 loading-gate
 * invariant). Until the server's `status` answers, protected routes are held
 * behind a neutral surface — never rendered first and corrected after, which
 * would flash the app at someone whose account is scheduled for deletion. A
 * failed lookup fails CLOSED to a retry screen that also offers sign-out, as
 * the role guards do (AuthGuardError).
 */

import type { DeletionState } from './account-deletion';

export type DeletionGateDecision = 'pass' | 'hold' | 'error' | 'redirect';

export const DELETION_PENDING_ROUTE = '/account-deletion-pending' as const;

/**
 * First route segments reachable while deletion is pending.
 *   account-deletion-pending — the destination itself.
 *   mfa-verify               — the sign-in step-up; it hands back to '/', and
 *                              the gate takes it from there. Redirecting out of
 *                              it mid-challenge would discard the session.
 *   login / (auth) / forgot-password / reset-password — getting in and out.
 *   legal pages              — what the person agreed to stays readable.
 */
export const DELETION_GATE_EXEMPT: readonly string[] = [
  'account-deletion-pending',
  'mfa-verify',
  'login',
  '(auth)',
  'forgot-password',
  'reset-password',
  'terms',
  'privacy',
  'disclaimer',
  'ai-disclosure',
  'about',
  'contact',
];

export function resolveDeletionGate(input: {
  signedIn: boolean;
  /** False in offline mock mode: there is no server-side state to consult. */
  backendAvailable: boolean;
  /** The status lookup failed with no answer in hand. */
  isError: boolean;
  /** undefined = not answered yet (loading); null = no open request. */
  deletion: DeletionState | null | undefined;
  /** First route segment (`useSegments()[0]`); undefined at '/'. */
  segment: string | undefined;
}): DeletionGateDecision {
  if (!input.signedIn || !input.backendAvailable) return 'pass';
  if (input.segment && DELETION_GATE_EXEMPT.includes(input.segment)) return 'pass';
  // A server answer already in hand decides, even if a later refetch failed:
  // a background blip must not lock out a user we know has no request.
  if (input.deletion !== undefined) return input.deletion ? 'redirect' : 'pass';
  if (input.isError) return 'error';
  return 'hold';
}
